import { selectTagsStandardProps } from "@/utils/componentUtils";
import { setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Alert, Checkbox, Col, Divider, Form, Input, Modal, Row, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useChangeContractVerificationStatus } from "../../../../common/api/queries";
import HiddenInput from "../../../../common/components/form/components/HiddenInput";
import { ModalSizes, rowGutter } from "../../../../common/constants";
import { ContractVerificationStatus, contractVerificationStatusTMap } from "../../enums";
import { ChangeContractVerificationStatus, Contract, ContractList } from "../../types";
import ContractVerificationStatusTag from "../tags/ContractVerificationStatusTag";

const INTERVENTION_STATUSES = [
  ContractVerificationStatus.INTERNAL_INTERVENTION,
  ContractVerificationStatus.EXTERNAL_INTERVENTION,
  ContractVerificationStatus.INTERNAL_AND_EXTERNAL_INTERVENTION,
];

const SELECTABLE_STATUSES = Object.values(ContractVerificationStatus).filter(
  (status) => status !== ContractVerificationStatus.PAID,
);

type Props = {
  open: boolean;
  contract?: Contract | ContractList;
  viewType?: "sendToBackoffice" | "noteEdit" | "default";
  onFormCancel: () => void;
};

const ContractChangeVerificationStatusForm = ({ open, contract, viewType, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const changeContractVerificationStatus = useChangeContractVerificationStatus();

  const [form] = Form.useForm<ChangeContractVerificationStatus>();
  const verificationStatus = Form.useWatch("verificationStatus", form);

  const selectableStatuses =
    contract?.verificationStatus === ContractVerificationStatus.PAID ? INTERVENTION_STATUSES : SELECTABLE_STATUSES;

  useEffect(() => {
    if (open && contract) {
      let formData: Partial<ChangeContractVerificationStatus> = {
        optimisticLockVersion: contract.optimisticLockVersion,
      };

      switch (viewType) {
        case "sendToBackoffice":
          formData = { ...formData, verificationStatus: ContractVerificationStatus.SENT };
          break;
        case "noteEdit":
          formData = {
            ...formData,
            verificationStatus: contract.verificationStatus,
            verificationNote: contract.verificationNote,
            verificationAttachmentsData: contract.verificationAttachmentsData,
          };
          break;
      }

      form.setFieldsValue(formData as ChangeContractVerificationStatus);
    }
  }, [open, contract, viewType, form]);

  const handleFormSubmit = () => {
    if (contract) {
      form
        .validateFields()
        .then((values) =>
          changeContractVerificationStatus.mutate(
            { id: contract.id, object: values },
            { onSuccess: onFormCancel, onError: (error) => setErrorsToForm(form, "contract.attrs", error.violations) },
          ),
        );
    }
  };

  return (
    <Modal
      width={ModalSizes.TINY}
      open={open}
      title={t("contract.helpers.verificationStatusChange")}
      cancelText={t("common.cancel")}
      okText={t("common.save")}
      confirmLoading={changeContractVerificationStatus.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name={"contractChangeVerificationStatusForm" + viewType}>
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item
              name="verificationStatus"
              label={t("contract.enums.verificationStatus._label")}
              rules={[validations.notNull]}
            >
              <Select
                {...selectTagsStandardProps(contractVerificationStatusTMap)}
                disabled={viewType === "sendToBackoffice" || viewType === "noteEdit"}
                options={(viewType === "sendToBackoffice" || viewType === "noteEdit"
                  ? Object.values(ContractVerificationStatus)
                  : selectableStatuses
                )?.map((status) => ({
                  value: status,
                  label: <ContractVerificationStatusTag status={status} />,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        {verificationStatus === ContractVerificationStatus.SENT ||
        verificationStatus === ContractVerificationStatus.INTERNAL_INTERVENTION ||
        verificationStatus === ContractVerificationStatus.EXTERNAL_INTERVENTION ||
        verificationStatus === ContractVerificationStatus.INTERNAL_AND_EXTERNAL_INTERVENTION ? (
          <>
            <Row>
              <Col span={24}>
                <Form.Item
                  name="verificationNote"
                  label={t("contract.attrs.verificationNote")}
                  rules={[validations.size(1, 1024)]}
                >
                  <Input.TextArea
                    placeholder={t("contract.helpers.verificationNotePlaceholder")}
                    autoSize={{ minRows: 3, maxRows: 15 }}
                  />
                </Form.Item>
              </Col>
            </Row>

            {verificationStatus === ContractVerificationStatus.SENT && (
              <>
                <Divider className="divider-subheader">
                  {t("contract.attrs.verificationAttachmentsData._label")}
                </Divider>

                <Row gutter={rowGutter}>
                  <Col span={24} className="margin-bottom-small">
                    <Form.Item
                      noStyle
                      name={["verificationAttachmentsData", "financialMediation"]}
                      valuePropName="checked"
                      rules={[validations.none]}
                      initialValue={false}
                    >
                      <Checkbox>{t("contract.attrs.verificationAttachmentsData.financialMediation")}</Checkbox>
                    </Form.Item>
                  </Col>

                  <Col span={24} className="margin-bottom-small">
                    <Form.Item
                      noStyle
                      name={["verificationAttachmentsData", "investmentServiceAgreement"]}
                      valuePropName="checked"
                      rules={[validations.none]}
                      initialValue={false}
                    >
                      <Checkbox>{t("contract.attrs.verificationAttachmentsData.investmentServiceAgreement")}</Checkbox>
                    </Form.Item>
                  </Col>

                  <Col span={24} className="margin-bottom-small">
                    <Form.Item
                      noStyle
                      name={["verificationAttachmentsData", "identityCardCopy"]}
                      valuePropName="checked"
                      rules={[validations.none]}
                      initialValue={false}
                    >
                      <Checkbox>{t("contract.attrs.verificationAttachmentsData.identityCardCopy")}</Checkbox>
                    </Form.Item>
                  </Col>

                  <Col span={24} className="margin-bottom-small">
                    <Form.Item
                      noStyle
                      name={["verificationAttachmentsData", "tipAgreement"]}
                      valuePropName="checked"
                      rules={[validations.none]}
                      initialValue={false}
                    >
                      <Checkbox>{t("contract.attrs.verificationAttachmentsData.tipAgreement")}</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
          </>
        ) : undefined}

        {contract?.commissionPaid && verificationStatus === ContractVerificationStatus.VERIFIED && (
          <Alert title={t("contract.helpers.commissionAlreadyPaid")} type="info" showIcon />
        )}
      </Form>
    </Modal>
  );
};

export default ContractChangeVerificationStatusForm;
